import { predicateSourceMigration } from "utils/filter";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { IProperty } from "hooks/useProperty";

import { LegendItemModel } from "models/LegendItem";
import { IPredicate, PropertyFilterModel } from "models/filter";
import { DataEnum, IGroupBy } from "models/groupBy";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

function extractLowerBound(value: string | number): number | null {
  if (typeof value === "number") return value;
  if (!value) return null;

  const rangeMatch = value.match(/^(\d+(?:\.\d+)?)\s*-\s*\d+/);
  if (rangeMatch) return parseFloat(rangeMatch[1]);

  const gtMatch = value.match(/^>=?\s*(\d+(?:\.\d+)?)/);
  if (gtMatch) return parseFloat(gtMatch[1]);

  if (value.match(/^<?=?\s*\d+/)) return null;

  const parsed = parseFloat(value);
  return isNaN(parsed) ? null : parsed;
}

function extractRangeBounds(
  value: string | number
): { lower: number; upper: number } | null {
  if (typeof value === "number") return null;
  if (!value) return null;

  const normalized = value.toString().trim();
  const rangeMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
  if (!rangeMatch) return null;

  const lower = parseFloat(rangeMatch[1]);
  const upper = parseFloat(rangeMatch[2]);
  if (Number.isNaN(lower) || Number.isNaN(upper)) return null;

  return { lower, upper };
}

function predicateMatchesLegendItem(
  predicate: IPredicate,
  legendItem: LegendItemModel,
  groupBy: IGroupBy
): boolean {
  if (predicate.property !== legendItem.property) return false;

  // The Others row's title changes with the bins, so it matches by the flag alone.
  if (predicate.rollup === "Others" || legendItem.isOthersRollup) {
    return predicate.rollup === "Others" && legendItem.isOthersRollup;
  }

  const sourceKey = predicate.sourceKey || predicateSourceMigration(predicate);

  if (sourceKey === legendItem.value) {
    return true;
  }

  const isIntegerDataType = groupBy.dataType === DataEnum.Integer;

  if (isIntegerDataType && predicate.bin && legendItem.bin && legendItem.canBin) {
    const predicateLowerBound = extractLowerBound(sourceKey);
    const legendLowerBound = extractLowerBound(legendItem.value);

    if (predicateLowerBound !== null && legendLowerBound !== null) {
      const isMatch = predicateLowerBound === legendLowerBound;
      return isMatch;
    }
  }

  return false;
}

function toLegendItem(
  items,
  groupBy: IGroupBy,
  propertiesFilter: PropertyFilterModel[],
  groupByTitle?: IProperty,
  normalizeBySettings?
): { legendItems: LegendItemModel[]; matchedPredicateIndices: Set<number> } {
  const predicates = propertiesFilter.flatMap((filter) => filter.predicates);
  const usedPredicateIndices = new Set<number>();

  const isIntegerDataType = groupBy.dataType === DataEnum.Integer;
  const binSize = Number(groupBy.bin?.BinSize);

  const filteredItems =
    isIntegerDataType && Number.isInteger(binSize)
      ? items.filter((item) => {
          const range = extractRangeBounds(item.title ?? item.value ?? "");
          if (!range) return true;
          return range.upper - range.lower <= binSize - 1;
        })
      : items;

  const legendItems = filteredItems.map((item) => {
    const legendItemModel = new LegendItemModel(
      item.groupTitle,
      groupBy.property,
      groupBy.canBin,
      groupBy.categoryId,
      groupBy.bin,
      item.inFilter,
      item.count,
      normalizeBySettings
    );

    legendItemModel.pdenSource =
      typeof groupBy.pdenSource === "string"
        ? PdenSourceEnum[groupBy.pdenSource]
        : parseInt(groupBy.pdenSource);
    legendItemModel.color = item.color;
    legendItemModel.paletteColor = item.paletteColor ?? item.color;
    legendItemModel.fontColor = "#555555";
    legendItemModel.groupByTitle = groupBy.title;
    legendItemModel.partialContextTitle = groupByTitle?.partialContextTitle;
    legendItemModel.isForecastToggleOn = groupByTitle?.isForecastToggleOn;
    legendItemModel.selectedForecastFolderName = groupByTitle?.forecastFolder;
    legendItemModel.forecastTrumpingSettings = groupBy.forecastTrumpingSettings;
    legendItemModel.performanceDateRange = groupBy.performanceDateRange;
    legendItemModel.isOthersRollup = isOthersGroupLabel(item.title);

    const matchIndex = predicates.findIndex(
      (predicate, index) =>
        !usedPredicateIndices.has(index) &&
        predicateMatchesLegendItem(predicate, legendItemModel, groupBy)
    );
    if (matchIndex >= 0) {
      usedPredicateIndices.add(matchIndex);
      legendItemModel.isChecked = true;
      legendItemModel.isNotEqual = predicates[matchIndex].negatedOperator;
    } else {
      legendItemModel.isChecked = false;
      legendItemModel.isNotEqual = false;
    }

    return legendItemModel;
  });

  return { legendItems: legendItems, matchedPredicateIndices: usedPredicateIndices };
}

export default toLegendItem;
